import { Suspense, useEffect, useRef, type KeyboardEvent } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { tr } from '../lib/i18n.ts';
import { useMediaQuery } from '../lib/useMediaQuery.ts';
import { SETTINGS_TABS } from './registry.ts';
import { neighbourTab, resolveTab, useSettings } from './store.ts';

const FOCUSABLE = 'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Ayarlar: a compact window under the bar's right edge on a desktop, a full-screen sheet on a phone
 * (the tab list first, a tab's page after). Tabs come from the registry.
 */
export function SettingsWindow() {
  const open = useSettings((state) => state.open);
  return open ? <Open /> : null;
}

function Open() {
  const phone = useMediaQuery('(max-width: 767px)');
  const tab = useSettings((state) => state.tab);
  const { close, selectTab, back } = useSettings.getState();
  const rootRef = useRef<HTMLDivElement>(null);
  const active = phone ? tab : resolveTab(tab);
  const activeTab = SETTINGS_TABS.find((item) => item.id === active);

  useEffect(() => {
    rootRef.current?.focus();
    return () => useSettings.getState().opener?.focus();
  }, []);

  // A phone starts on the list; moving between the list and a page keeps focus inside the sheet.
  useEffect(() => {
    if (phone) rootRef.current?.focus();
  }, [phone, active]);

  function onKeyDown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      event.stopPropagation();
      if (phone && tab) back();
      else close();
      return;
    }
    if (event.key !== 'Tab') return;
    const items = [...(rootRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? [])];
    if (items.length === 0) return event.preventDefault();
    const index = items.indexOf(document.activeElement as HTMLElement);
    const next = index === -1 ? (event.shiftKey ? items.length - 1 : 0) : (index + (event.shiftKey ? -1 : 1) + items.length) % items.length;
    event.preventDefault();
    items[next]?.focus();
  }

  function onTabKey(event: KeyboardEvent, id: (typeof SETTINGS_TABS)[number]['id']) {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    event.preventDefault();
    const next = neighbourTab(id, event.key === 'ArrowDown' ? 1 : -1);
    selectTab(next);
    document.getElementById(`settings-tab-${next}`)?.focus();
  }

  const showList = !phone || !tab;
  const showPage = !phone || Boolean(tab);
  const title = phone && activeTab ? activeTab.label : tr.settings.title;

  return (
    <div className="settings-layer" data-phone={phone || undefined} onPointerDown={(event) => event.target === event.currentTarget && close()}>
      <div ref={rootRef} className="settings-window" role="dialog" aria-modal="true" aria-label={tr.settings.title} tabIndex={-1} onKeyDown={onKeyDown}>
        <header className="settings-head">
          {phone && tab ? (
            <button type="button" className="icon-button" aria-label={tr.settings.back} onClick={back}>
              <ChevronLeft size={18} aria-hidden />
            </button>
          ) : null}
          <h2>{title}</h2>
          <button type="button" className="icon-button" aria-label={tr.settings.close} onClick={close}>
            <X size={16} aria-hidden />
          </button>
        </header>
        <div className="settings-body">
          {showList && (
            <nav className="settings-tabs" role="tablist" aria-orientation="vertical" aria-label={tr.settings.tabList}>
              {SETTINGS_TABS.map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  id={`settings-tab-${id}`}
                  type="button"
                  role="tab"
                  aria-selected={active === id}
                  aria-controls="settings-page"
                  tabIndex={phone || active === id ? 0 : -1}
                  className="settings-tab"
                  onClick={() => selectTab(id)}
                  onKeyDown={(event) => onTabKey(event, id)}
                >
                  <Icon size={16} aria-hidden />
                  <span>{label}</span>
                  {phone && <ChevronRight size={16} aria-hidden className="settings-tab-chevron" />}
                </button>
              ))}
            </nav>
          )}
          {showPage && activeTab && (
            <section id="settings-page" className="settings-page" role={phone ? undefined : 'tabpanel'} aria-labelledby={phone ? undefined : `settings-tab-${activeTab.id}`}>
              <Suspense fallback={<p className="s-hint">{tr.settings.loading}</p>}>
                <activeTab.Panel />
              </Suspense>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
